import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

import { Buildings, PencilSimple, CaretRight, CaretDown, CaretUp, EnvelopeSimple, CreditCard, ArrowLeft, FileText, Bell, UsersThree, Gavel, DeviceMobile, Lightning, Hash, CloudArrowUp } from '@phosphor-icons/react';
import NumberingSection from '../components/NumberingSection';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_TEMPLATES = {
  invoice_subject: 'Common Expenses – {invoice_number} – {company_name}',
  invoice_body: 'Dear {owner_name},\n\nPlease find attached the invoice {invoice_number} regarding the common expenses for apartment {apartment_number} for the month of {month}.\n\nBest regards,\n{company_name}',
  payment_subject: 'Payment Receipt – {apartment_number} – {company_name}',
  payment_body: 'Dear {owner_name},\n\nPlease find attached the payment receipt for apartment {apartment_number}.\n\nBest regards,\n{company_name}',
  statement_subject: 'Common Expenses Statement – {apartment_number} – Period {start_date} to {end_date} – {company_name}',
  statement_body: 'Dear {owner_name},\n\nPlease find attached your common expenses statement for apartment {apartment_number} for the period from {start_date} to {end_date}.\n\nBest regards,\n{company_name}',
  meeting_subject: 'General Meeting Notice – {building_name} – {meeting_date} – {company_name}',
  meeting_body: 'Dear Owners,\n\nPlease find attached the minutes of the {meeting_type} for {building_name} held on {meeting_date}.\n\nBest regards,\n{company_name}',
};

function CompanyProfileSection({ t, isAdmin }) {
  const [profile, setProfile] = useState({
    company_name: '', company_name_line2: '',
    company_street_address: '', company_zip_code: '', company_city: '', company_country: '',
    company_email: '', company_phone: '', company_vat_number: '',
    // Phase 488 — Company Branding
    company_registration_number: '',
    company_website: '',
    company_logo: '',
  });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const logoFileRef = React.useRef(null);
  // Phase 488.1 — Increased ceiling. Files between RAW_LIMIT and HARD_LIMIT
  // are auto-compressed via a hidden canvas (JPEG quality 0.85, max
  // 1200×600). Anything above HARD_LIMIT is rejected outright.
  const RAW_LIMIT = 2 * 1024 * 1024;    // 2 MB — uploaded as-is when smaller
  const HARD_LIMIT = 10 * 1024 * 1024;  // 10 MB — absolute cap (pre-compression)

  useEffect(() => {
    axios.get(`${API}/company-profile`).then(r => { setProfile(prev => ({ ...prev, ...r.data })); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try { await axios.put(`${API}/company-profile`, profile); toast.success(t('savedSuccess')); setEditing(false); }
    catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // Phase 488.1 — Compress/resize a large bitmap via an off-screen canvas
  // so the user doesn't have to manually shrink huge phone-camera photos
  // they upload as logos. SVGs are returned untouched (vector).
  const compressImage = (file) => new Promise((resolve, reject) => {
    if (file.type === 'image/svg+xml') {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1200, maxH = 600;
        let { width, height } = img;
        const ratio = Math.min(maxW / width, maxH / height, 1);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        // PNG preserves transparency; JPEG only used when the source was JPEG.
        const outType = (file.type === 'image/jpeg' || file.type === 'image/jpg') ? 'image/jpeg' : 'image/png';
        const dataUrl = canvas.toDataURL(outType, 0.85);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Image decode failed'));
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const fmtBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Phase 488.1 — Robust upload handler. The file input is reset on EVERY
  // exit path (validation failure, success, error) so the user can
  // immediately retry — fixing the bug where rejecting a file would
  // freeze the uploader until page reload.
  const handleLogoFile = async (file) => {
    // Always reset the underlying <input> so the same filename can be
    // picked again after this call returns.
    const resetInput = () => { if (logoFileRef.current) logoFileRef.current.value = ''; };
    if (!file) { resetInput(); return; }

    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error(`${t('logoFormatError') || 'Logo must be PNG, JPG, JPEG or SVG'} — ${file.type || 'unknown'}`);
      resetInput();
      return;
    }
    if (file.size > HARD_LIMIT) {
      toast.error(`${t('logoSizeError') || 'Logo file is too large.'} (${fmtBytes(file.size)} > ${fmtBytes(HARD_LIMIT)})`);
      resetInput();
      return;
    }

    setUploadingLogo(true);
    setUploadProgress(10);
    try {
      let dataUrl;
      if (file.type === 'image/svg+xml' || file.size <= RAW_LIMIT) {
        // Small enough — upload bytes verbatim.
        dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onprogress = (ev) => { if (ev.lengthComputable) setUploadProgress(10 + Math.round((ev.loaded / ev.total) * 40)); };
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
      } else {
        // Large bitmap — auto-compress via canvas before saving.
        setUploadProgress(30);
        dataUrl = await compressImage(file);
        toast.info(`${t('logoCompressed') || 'Logo auto-compressed'} (${fmtBytes(file.size)} → ${fmtBytes(Math.round(dataUrl.length * 0.75))})`);
      }
      setUploadProgress(70);
      await axios.put(`${API}/company-profile`, { ...profile, company_logo: dataUrl });
      setProfile(p => ({ ...p, company_logo: dataUrl }));
      setUploadProgress(100);
      toast.success(t('logoUpdated') || 'Logo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || (t('errorOccurred') || 'Error'));
    } finally {
      setUploadingLogo(false);
      // Brief delay so users see the 100% bar before it disappears.
      setTimeout(() => setUploadProgress(0), 600);
      resetInput();
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.put(`${API}/company-profile`, { ...profile, company_logo: '' });
      setProfile(p => ({ ...p, company_logo: '' }));
      toast.success(t('logoRemoved') || 'Logo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  if (loading) return <div className="p-8 text-center text-zinc-400">{t('loading')}</div>;

  return (
    <div className="bg-white border border-zinc-200" data-testid="company-profile-section">
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Buildings size={20} weight="fill" className="text-zinc-600" />
          <h2 className="text-sm font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('companyProfile')}</h2>
        </div>
        {isAdmin && !editing && <button onClick={() => setEditing(true)} className="text-zinc-400 hover:text-zinc-700" data-testid="edit-profile-btn"><PencilSimple size={16} /></button>}
      </div>

      {/* Phase 488 — Branding panel (logo + key brand fields) always visible */}
      <div className="px-5 py-4 border-b border-zinc-100 bg-gradient-to-r from-zinc-50 to-white" data-testid="branding-panel">
        <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-3">{t('branding') || 'Branding'}</p>
        <div className="flex items-center gap-5">
          <div className="w-32 h-20 border-2 border-dashed border-zinc-300 bg-white flex items-center justify-center overflow-hidden" data-testid="logo-preview">
            {profile.company_logo ? (
              <img src={profile.company_logo} alt="Company logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-[10px] text-zinc-400 text-center px-2">{t('noLogoUploaded') || 'No logo uploaded'}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-800">{profile.company_name || '-'}</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t('logoHelperText') || 'Appears on all invoices, statements, PDFs, and emails.'}
              {' '}<span className="font-mono text-[10px] text-zinc-400">PNG · JPG · SVG · max {fmtBytes(HARD_LIMIT)}</span>
            </p>
            {/* Progress bar */}
            {uploadingLogo && (
              <div className="mt-2 h-1.5 bg-zinc-100 overflow-hidden" data-testid="logo-upload-progress-bar">
                <div className="h-full bg-emerald-500 transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
              </div>
            )}
            {isAdmin && (
              <div className="flex gap-2 mt-3">
                <input
                  ref={logoFileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={e => handleLogoFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="logo-file-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-none text-xs"
                  disabled={uploadingLogo}
                  onClick={() => logoFileRef.current?.click()}
                  data-testid={profile.company_logo ? 'logo-replace-btn' : 'logo-upload-btn'}
                >
                  {uploadingLogo
                    ? `${t('uploading') || 'Uploading...'} ${uploadProgress}%`
                    : profile.company_logo ? (t('replaceLogo') || 'Replace Logo') : (t('uploadLogo') || 'Upload Logo')}
                </Button>
                {profile.company_logo && !uploadingLogo && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-none text-xs text-red-600 hover:bg-red-50 border-red-200"
                    onClick={handleRemoveLogo}
                    data-testid="logo-remove-btn"
                  >
                    {t('removeLogo') || 'Remove'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {editing ? (
        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div><Label className="text-xs">{t('companyName')}</Label><Input value={profile.company_name} onChange={e => setProfile({ ...profile, company_name: e.target.value })} className="mt-1 rounded-none" data-testid="profile-company-name" /></div>
          <div><Label className="text-xs">{t('companyNameLine2')}</Label><Input value={profile.company_name_line2} onChange={e => setProfile({ ...profile, company_name_line2: e.target.value })} className="mt-1 rounded-none" placeholder={t('optional')} data-testid="profile-company-name-line2" /></div>
          <div className="space-y-3 border-t border-zinc-200 pt-3">
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{t('address')}</p>
            <div><Label className="text-xs">{t('streetAddress')}</Label><Input value={profile.company_street_address} onChange={e => setProfile({ ...profile, company_street_address: e.target.value })} className="mt-1 rounded-none" data-testid="profile-street" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">{t('zipCode')}</Label><Input value={profile.company_zip_code} onChange={e => setProfile({ ...profile, company_zip_code: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="profile-zip" /></div>
              <div><Label className="text-xs">{t('city')}</Label><Input value={profile.company_city} onChange={e => setProfile({ ...profile, company_city: e.target.value })} className="mt-1 rounded-none" data-testid="profile-city" /></div>
              <div><Label className="text-xs">{t('countryRegion')}</Label><Input value={profile.company_country} onChange={e => setProfile({ ...profile, company_country: e.target.value })} className="mt-1 rounded-none" data-testid="profile-country" /></div>
            </div>
          </div>
          <div className="space-y-3 border-t border-zinc-200 pt-3">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs">{t('email')}</Label><Input type="email" value={profile.company_email} onChange={e => setProfile({ ...profile, company_email: e.target.value })} className="mt-1 rounded-none" data-testid="profile-email" /></div>
              <div><Label className="text-xs">{t('companyPhone')}</Label><Input value={profile.company_phone} onChange={e => setProfile({ ...profile, company_phone: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="profile-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs">{t('website') || 'Website'}</Label><Input type="url" value={profile.company_website} onChange={e => setProfile({ ...profile, company_website: e.target.value })} className="mt-1 rounded-none" placeholder="https://" data-testid="profile-website" /></div>
              <div><Label className="text-xs">{t('companyTicNo')}</Label><Input value={profile.company_registration_number} onChange={e => setProfile({ ...profile, company_registration_number: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="profile-registration" /></div>
            </div>
            <div><Label className="text-xs">{t('companyVatRegNo')}</Label><Input value={profile.company_vat_number} onChange={e => setProfile({ ...profile, company_vat_number: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="profile-vat" /></div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={() => setEditing(false)} className="rounded-none">{t('cancel')}</Button>
            <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-profile-btn">{t('save')}</Button>
          </div>
        </form>
      ) : (
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
            <div className="flex items-baseline justify-between py-2 border-b border-zinc-100"><span className="text-sm text-zinc-500">{t('companyName')}</span><span className="text-sm font-medium text-zinc-900">{profile.company_name || '-'}</span></div>
            {profile.company_name_line2 && <div className="flex items-baseline justify-between py-2 border-b border-zinc-100"><span className="text-sm text-zinc-500">{t('companyNameLine2')}</span><span className="text-sm font-medium text-zinc-900">{profile.company_name_line2}</span></div>}
            <div className="flex items-baseline justify-between py-2 border-b border-zinc-100 sm:col-span-2"><span className="text-sm text-zinc-500">{t('companyAddress')}</span><span className="text-sm font-medium text-zinc-900">{[profile.company_street_address, profile.company_zip_code, profile.company_city, profile.company_country].filter(Boolean).join(', ') || '-'}</span></div>
            <div className="flex items-baseline justify-between py-2 border-b border-zinc-100"><span className="text-sm text-zinc-500">{t('email')}</span><span className="text-sm font-medium text-zinc-900 font-mono">{profile.company_email || '-'}</span></div>
            <div className="flex items-baseline justify-between py-2 border-b border-zinc-100"><span className="text-sm text-zinc-500">{t('companyPhone')}</span><span className="text-sm font-medium text-zinc-900 font-mono">{profile.company_phone || '-'}</span></div>
            <div className="flex items-baseline justify-between py-2 border-b border-zinc-100"><span className="text-sm text-zinc-500">{t('website') || 'Website'}</span><span className="text-sm font-medium text-zinc-900 font-mono">{profile.company_website || '-'}</span></div>
            <div className="flex items-baseline justify-between py-2 border-b border-zinc-100"><span className="text-sm text-zinc-500">{t('companyTicNo')}</span><span className="text-sm font-medium text-zinc-900 font-mono">{profile.company_registration_number || '-'}</span></div>
            <div className="flex items-baseline justify-between py-2 border-b border-zinc-100"><span className="text-sm text-zinc-500">{t('companyVatRegNo')}</span><span className="text-sm font-medium text-zinc-900 font-mono">{profile.company_vat_number || '-'}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateCard({ title, icon: Icon, subjectKey, bodyKey, settings, onSave, t, isAdmin, defaultSubject, defaultBody }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(settings[subjectKey] || defaultSubject);
  const [body, setBody] = useState(settings[bodyKey] || defaultBody);

  useEffect(() => {
    setSubject(settings[subjectKey] || defaultSubject);
    setBody(settings[bodyKey] || defaultBody);
  }, [settings, subjectKey, bodyKey, defaultSubject, defaultBody]);

  const handleSave = async () => {
    await onSave({ [subjectKey]: subject, [bodyKey]: body });
    setEditing(false);
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid={`template-${subjectKey}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors text-left">
        <Icon size={16} className="text-zinc-500 flex-shrink-0" />
        <span className="text-sm font-medium text-zinc-800 flex-1">{title}</span>
        {expanded ? <CaretUp size={14} className="text-zinc-400" /> : <CaretDown size={14} className="text-zinc-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-zinc-100 pt-3 space-y-3">
          {editing ? (
            <>
              <div><Label className="text-xs">{t('defaultSubjectTemplate')}</Label><Input value={subject} onChange={e => setSubject(e.target.value)} className="mt-1 rounded-none text-sm font-mono" /></div>
              <div><Label className="text-xs">{t('defaultBodyTemplate')}</Label><Textarea value={body} onChange={e => setBody(e.target.value)} className="mt-1 rounded-none text-sm" rows={5} /></div>
              <p className="text-[10px] text-zinc-400">{t('templateHint')}</p>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setSubject(settings[subjectKey] || defaultSubject); setBody(settings[bodyKey] || defaultBody); }} className="rounded-none text-xs">{t('cancel')}</Button>
                <Button size="sm" onClick={handleSave} className="bg-zinc-950 rounded-none text-xs">{t('save')}</Button>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="text-[10px] text-zinc-400 uppercase">{t('defaultSubjectTemplate')}</p>
                <p className="text-sm font-mono text-zinc-700 mt-1 bg-zinc-50 px-3 py-2 border border-zinc-200">{subject}</p>
              </div>
              <div>
                <p className="text-[10px] text-zinc-400 uppercase">{t('defaultBodyTemplate')}</p>
                <p className="text-sm text-zinc-700 mt-1 bg-zinc-50 px-3 py-2 border border-zinc-200 whitespace-pre-line">{body}</p>
              </div>
              {isAdmin && (
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-none text-xs gap-1"><PencilSimple size={12} />{t('edit')}</Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function EmailSettingsSection({ t, isAdmin }) {
  const [settings, setSettings] = useState({});
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ brevo_api_key: '', sender_email: '', sender_name: '' });
  const [loading, setLoading] = useState(true);

  const fetchSettings = () => axios.get(`${API}/email-settings`).then(r => { setSettings(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { fetchSettings(); }, []);

  const startEdit = () => {
    setForm({
      brevo_api_key: settings.brevo_api_key_masked || '',
      sender_email: settings.sender_email || '',
      sender_name: settings.sender_name || '',
    });
    setEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try { await axios.put(`${API}/email-settings`, form); toast.success(t('emailConfigSaved')); setEditing(false); fetchSettings(); }
    catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const handleTemplateSave = async (data) => {
    try { await axios.put(`${API}/email-settings`, data); toast.success(t('savedSuccess')); fetchSettings(); }
    catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  if (loading) return <div className="p-8 text-center text-zinc-400">{t('loading')}</div>;

  return (
    <div className="space-y-6">
      {/* Connection Settings */}
      <div className="bg-white border border-zinc-200" data-testid="email-connection-section">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <EnvelopeSimple size={20} weight="fill" className="text-zinc-600" />
            <h2 className="text-sm font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('emailSettings')}</h2>
          </div>
          {isAdmin && !editing && <button onClick={startEdit} className="text-zinc-400 hover:text-zinc-700" data-testid="edit-email-settings-btn"><PencilSimple size={16} /></button>}
        </div>
        {editing ? (
          <form onSubmit={handleSave} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">{t('emailProvider') || 'Email Provider'}</Label>
                <Input value="Brevo" disabled className="mt-1 rounded-none bg-zinc-50 text-zinc-700 font-medium" data-testid="email-provider-fixed" />
              </div>
              <div>
                <Label className="text-xs">{t('brevoApiKey') || 'Brevo API Key'}</Label>
                <Input type="password" value={form.brevo_api_key} onChange={e => setForm({ ...form, brevo_api_key: e.target.value })} className="mt-1 rounded-none font-mono" placeholder="xkeysib-..." data-testid="email-brevo-api-key" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs">{t('senderEmail')}</Label><Input type="email" value={form.sender_email} onChange={e => setForm({ ...form, sender_email: e.target.value })} className="mt-1 rounded-none" data-testid="email-sender-email" /></div>
              <div><Label className="text-xs">{t('senderName')}</Label><Input value={form.sender_name} onChange={e => setForm({ ...form, sender_name: e.target.value })} className="mt-1 rounded-none" data-testid="email-sender-name" /></div>
            </div>
            <p className="text-[11px] text-zinc-400 italic">
              {t('brevoHint') || 'Brevo uses POST https://api.brevo.com/v3/smtp/email with an api-key header. Make sure the sender email is a verified sender in your Brevo account.'}
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setEditing(false)} className="rounded-none">{t('cancel')}</Button>
              <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-email-settings-btn">{t('save')}</Button>
            </div>
          </form>
        ) : (
          <div className="p-5">
            <div className="grid grid-cols-2 gap-x-10 gap-y-3">
              <div className="flex items-baseline justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-500">{t('emailProvider')}</span>
                <span className="text-sm font-medium">Brevo</span>
              </div>
              <div className="flex items-baseline justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-500">{t('apiKey')}</span>
                <span className="text-sm font-mono">{settings.brevo_api_key_masked || '-'}</span>
              </div>
              <div className="flex items-baseline justify-between py-2 border-b border-zinc-100"><span className="text-sm text-zinc-500">{t('senderEmail')}</span><span className="text-sm font-mono">{settings.sender_email || '-'}</span></div>
              <div className="flex items-baseline justify-between py-2 border-b border-zinc-100"><span className="text-sm text-zinc-500">{t('senderName')}</span><span className="text-sm">{settings.sender_name || '-'}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Email Templates */}
      <div data-testid="email-templates-section">
        <h3 className="text-sm font-semibold text-zinc-700 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{t('emailTemplates')}</h3>
        <div className="space-y-2">
          <TemplateCard title={t('invoiceNotifications')} icon={FileText} subjectKey="invoice_subject_template" bodyKey="invoice_body_template" settings={settings} onSave={handleTemplateSave} t={t} isAdmin={isAdmin} defaultSubject={DEFAULT_TEMPLATES.invoice_subject} defaultBody={DEFAULT_TEMPLATES.invoice_body} />
          <TemplateCard title={t('paymentNotification')} icon={Bell} subjectKey="payment_subject_template" bodyKey="payment_body_template" settings={settings} onSave={handleTemplateSave} t={t} isAdmin={isAdmin} defaultSubject={DEFAULT_TEMPLATES.payment_subject} defaultBody={DEFAULT_TEMPLATES.payment_body} />
          <TemplateCard title={t('customerStatement')} icon={FileText} subjectKey="statement_subject_template" bodyKey="statement_body_template" settings={settings} onSave={handleTemplateSave} t={t} isAdmin={isAdmin} defaultSubject={DEFAULT_TEMPLATES.statement_subject} defaultBody={DEFAULT_TEMPLATES.statement_body} />
          <TemplateCard title={t('generalMeetings')} icon={UsersThree} subjectKey="meeting_subject_template" bodyKey="meeting_body_template" settings={settings} onSave={handleTemplateSave} t={t} isAdmin={isAdmin} defaultSubject={DEFAULT_TEMPLATES.meeting_subject} defaultBody={DEFAULT_TEMPLATES.meeting_body} />
        </div>
      </div>
    </div>
  );
}

function MeetingTemplatesSection({ t, isAdmin }) {
  // Phase 192/209j — Central meeting text templates: Legal Quorum, Committee
  // Decision, and the new Epilogue Templates (GR + EN). Edited here and
  // consumed by every meeting (UI preview + PDF + English Translate Dialog).
  const [form, setForm] = useState({
    legal_text: '',
    committee_text: '',
    epilogue_text_el: '',
    epilogue_text_en: '',
  });
  const [defaults, setDefaults] = useState({
    legal_text: '',
    committee_text: '',
    epilogue_text_el: '',
    epilogue_text_en: '',
  });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/meeting-text-templates`).then(r => {
      setForm({
        legal_text: r.data?.legal_text || '',
        committee_text: r.data?.committee_text || '',
        epilogue_text_el: r.data?.epilogue_text_el || '',
        epilogue_text_en: r.data?.epilogue_text_en || '',
      });
      setDefaults({
        legal_text: r.data?.legal_text_default || '',
        committee_text: r.data?.committee_text_default || '',
        epilogue_text_el: r.data?.epilogue_text_el_default || '',
        epilogue_text_en: r.data?.epilogue_text_en_default || '',
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/meeting-text-templates`, form);
      toast.success(t('savedSuccess'));
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const resetToDefault = (field) => {
    setForm(prev => ({ ...prev, [field]: defaults[field] || '' }));
  };

  if (loading) return <div className="p-8 text-center text-zinc-400">{t('loading')}</div>;

  return (
    <div className="bg-white border border-zinc-200" data-testid="meeting-templates-section">
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gavel size={20} weight="fill" className="text-zinc-600" />
          <div>
            <h2 className="text-sm font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Πρότυπα Κειμένων Συνελεύσεων</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Τα παρακάτω πρότυπα εφαρμόζονται αυτόματα σε όλα τα meetings</p>
          </div>
        </div>
        {isAdmin && !editing && (
          <button onClick={() => setEditing(true)} className="text-zinc-400 hover:text-zinc-700" data-testid="edit-meeting-templates-btn">
            <PencilSimple size={16} />
          </button>
        )}
      </div>
      {editing ? (
        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold">Νομικό Κείμενο Απαρτίας</Label>
              <button type="button" onClick={() => resetToDefault('legal_text')} className="text-[11px] text-zinc-500 hover:text-zinc-900" data-testid="reset-legal-template">
                Επαναφορά σε default
              </button>
            </div>
            <Textarea
              value={form.legal_text}
              onChange={e => setForm({ ...form, legal_text: e.target.value })}
              className="mt-1 rounded-none text-sm"
              rows={4}
              data-testid="legal-template-input"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold">Κείμενο Απόφασης Επιτροπής</Label>
              <button type="button" onClick={() => resetToDefault('committee_text')} className="text-[11px] text-zinc-500 hover:text-zinc-900" data-testid="reset-committee-template">
                Επαναφορά σε default
              </button>
            </div>
            <Textarea
              value={form.committee_text}
              onChange={e => setForm({ ...form, committee_text: e.target.value })}
              className="mt-1 rounded-none text-sm"
              rows={3}
              data-testid="committee-template-input"
            />
          </div>
          {/* Phase 209j — Epilogue Templates (GR + EN). Pre-fill the meeting
              editor textarea AND drive the PDF when the user has not
              customised the epilogue per-meeting. The editor and the PDF
              always render the same text. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold">Πρότυπο Επιλόγου (Ελληνικά) / Epilogue Template (GR)</Label>
              <button type="button" onClick={() => resetToDefault('epilogue_text_el')} className="text-[11px] text-zinc-500 hover:text-zinc-900" data-testid="reset-epilogue-el-template">
                Επαναφορά σε default
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mb-1">
              Αυτό το κείμενο προ-συμπληρώνεται αυτόματα στον επίλογο κάθε νέας συνέλευσης και τυπώνεται στο PDF — εκτός αν το επεξεργαστείτε ανά συνέλευση.
            </p>
            <Textarea
              value={form.epilogue_text_el}
              onChange={e => setForm({ ...form, epilogue_text_el: e.target.value })}
              className="mt-1 rounded-none text-sm font-mono"
              rows={8}
              data-testid="epilogue-el-template-input"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold">Πρότυπο Επιλόγου (Αγγλικά) / Epilogue Template (EN)</Label>
              <button type="button" onClick={() => resetToDefault('epilogue_text_en')} className="text-[11px] text-zinc-500 hover:text-zinc-900" data-testid="reset-epilogue-en-template">
                Επαναφορά σε default
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mb-1">
              Used as the default epilogue when the English Meeting PDF is generated for owners who don't read Greek.
            </p>
            <Textarea
              value={form.epilogue_text_en}
              onChange={e => setForm({ ...form, epilogue_text_en: e.target.value })}
              className="mt-1 rounded-none text-sm font-mono"
              rows={8}
              data-testid="epilogue-en-template-input"
            />
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-zinc-100">
            <Button type="button" variant="outline" onClick={() => setEditing(false)} className="rounded-none">{t('cancel')}</Button>
            <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-meeting-templates-btn">{t('save')}</Button>
          </div>
        </form>
      ) : (
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Νομικό Κείμενο Απαρτίας</p>
            <p className="text-sm text-zinc-700 bg-zinc-50 px-3 py-2 border border-zinc-200 whitespace-pre-wrap leading-relaxed" data-testid="legal-template-preview">
              {form.legal_text || defaults.legal_text || '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Κείμενο Απόφασης Επιτροπής</p>
            <p className="text-sm text-zinc-700 bg-zinc-50 px-3 py-2 border border-zinc-200 whitespace-pre-wrap leading-relaxed" data-testid="committee-template-preview">
              {form.committee_text || defaults.committee_text || '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Πρότυπο Επιλόγου (Ελληνικά)</p>
            <p className="text-sm text-zinc-700 bg-zinc-50 px-3 py-2 border border-zinc-200 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto" data-testid="epilogue-el-template-preview">
              {form.epilogue_text_el || defaults.epilogue_text_el || '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Πρότυπο Επιλόγου (Αγγλικά)</p>
            <p className="text-sm text-zinc-700 bg-zinc-50 px-3 py-2 border border-zinc-200 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto" data-testid="epilogue-en-template-preview">
              {form.epilogue_text_en || defaults.epilogue_text_en || '—'}
            </p>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-zinc-100 text-[11px] text-zinc-500 italic">
            <FileText size={12} />
            <span>Η «Παράγραφος Απαρτίας &amp; Διάρκειας» δεν είναι πρότυπο — υπολογίζεται αυτόματα από την απαρτία κάθε συνέλευσης.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Phase 301 — Mobile / Push diagnostics section.
// Shows the FCM status (whether Firebase service account JSON is wired)
// and exposes two admin buttons:
//   - "Send Test Push"   → POST /api/fcm/test  (push to the calling admin's devices)
//   - "Run Push Sweep"   → POST /api/fcm/scan-events
//                          (fires push for overdue invoices + insurance/
//                           elevator-cert/elevator-contract expiries)
// Single source of truth: same backend, same DB, same /api. No mobile
// side-store, no offline duplicates.
// =====================================================================
function MobilePushSection({ t, isAdmin }) {
  const [status, setStatus] = useState({ loading: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/fcm/status`);
        if (mounted) setStatus({ loading: false, ...res.data });
      } catch (e) {
        if (mounted) setStatus({ loading: false, enabled: false, reason: 'request failed' });
      }
    })();
    return () => { mounted = false; };
  }, []);

  const sendTest = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/fcm/test`);
      const d = res.data || {};
      if (d.skipped_no_fcm) {
        toast.error('Firebase δεν είναι ενεργοποιημένο — λείπει το service account JSON.');
      } else if (d.sent > 0) {
        toast.success(`Στάλθηκε σε ${d.sent} συσκευή(ές).`);
      } else {
        toast(`Καμία ενεργή συσκευή. ${d.reason || ''}`);
      }
    } catch (e) {
      toast.error('Αποτυχία αποστολής test push.');
    } finally {
      setBusy(false);
    }
  };

  const runSweep = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/fcm/scan-events`);
      const p = (res.data || {}).pushed || {};
      toast.success(`Σάρωση ολοκληρώθηκε: ${p.overdue || 0} ληξιπρόθεσμα, ${p.insurance || 0} ασφάλειες, ${p.elev_cert || 0} ανελκυστήρες (πιστ.), ${p.elev_contract || 0} ανελκυστήρες (συμβ.).`);
    } catch (e) {
      toast.error('Αποτυχία σάρωσης ειδοποιήσεων.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="mobile-push-section">
      <h2 className="text-base font-semibold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Mobile App · Push Notifications</h2>
      <p className="text-xs text-zinc-500 mb-4">
        Διαγνωστικά για το Android app. Όλα τα δεδομένα προέρχονται από την ίδια βάση δεδομένων και API με τον ιστότοπο.
      </p>

      {/* Status */}
      <div className="border border-zinc-200 p-3 mb-4 bg-zinc-50 text-sm" data-testid="fcm-status">
        {status.loading ? (
          <span className="text-zinc-400">Φόρτωση κατάστασης…</span>
        ) : status.enabled ? (
          <span className="text-zinc-800"><span className="inline-block w-2 h-2 bg-emerald-500 rounded-full mr-2 align-middle" />Firebase ενεργό — οι ειδοποιήσεις παραδίδονται κανονικά.</span>
        ) : (
          <span className="text-zinc-700"><span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2 align-middle" />Firebase ανενεργό. <span className="text-zinc-400">{status.reason}</span></span>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={!isAdmin || busy}
          onClick={sendTest}
          className="rounded-none"
          data-testid="fcm-test-push-btn"
        >
          <Bell size={14} className="mr-2" />
          Send Test Push
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={!isAdmin || busy}
          onClick={runSweep}
          className="rounded-none"
          data-testid="fcm-sweep-btn"
        >
          <Lightning size={14} className="mr-2" />
          Run Push Sweep
        </Button>
      </div>

      <p className="text-[11px] text-zinc-400 mt-4 leading-relaxed">
        Το "Send Test Push" στέλνει μία δοκιμαστική ειδοποίηση στις δικές σου συσκευές. Το "Run Push Sweep" σαρώνει
        ληξιπρόθεσμα τιμολόγια & ασφάλειες/ανελκυστήρες που λήγουν σε ≤30 ημέρες και στέλνει push σε όλους τους admin.
      </p>
    </div>
  );
}

export default function Settings() {  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const [activeSection, setActiveSection] = useState(null);

  // Phase 192 — Deep-link via ?section=meetingTemplates from the Meetings form.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    if (section) setActiveSection(section);
  }, []);

  const sections = [
    { id: 'companyProfile', icon: Buildings, label: t('companyProfile'), desc: t('companyProfileHint') },
    { id: 'numbering', icon: Hash, label: 'Numbering Sequences', desc: 'Custom prefixes, padding & reset rules for invoices, payments and bills', adminOnly: true },
    { id: 'mobilePush', icon: DeviceMobile, label: 'Mobile · Push', desc: 'Status & test the Android push notification pipeline', adminOnly: true },
    { id: 'backups', icon: CloudArrowUp, label: 'Backup & Restore', desc: 'Dropbox connection, manual backups, backup history & audit log', adminOnly: true, route: '/settings/backups' },
    { id: 'manageSubscription', icon: CreditCard, label: t('manageSubscription'), desc: t('manageSubscriptionHint'), comingSoon: true },
    // Phase 207g — "Email Settings" was moved out of Organization Settings;
    // it now lives centrally in Manage Buildings → Settings (alongside Meeting
    // Templates) because it is a building-operational concern, not a company-
    // level config.
  ].filter(s => !s.adminOnly || isAdmin);

  if (!activeSection) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-8" data-testid="settings-page">
          <h1 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{t('settings')}</h1>
          <p className="text-sm text-zinc-400 mb-6">{t('organizationSettings')}</p>
          <div className="space-y-2" data-testid="settings-sections">
            {sections.map(sec => (
              <button key={sec.id} onClick={() => { if (sec.comingSoon) return; if (sec.route) { window.location.href = sec.route; } else { setActiveSection(sec.id); } }}
                className={`w-full bg-white border border-zinc-200 px-5 py-4 flex items-center gap-4 text-left group transition-colors ${sec.comingSoon ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-50'}`}
                data-testid={`settings-section-${sec.id}`} disabled={sec.comingSoon}>
                <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center flex-shrink-0"><sec.icon size={20} weight="fill" className="text-zinc-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-zinc-800">{sec.label}</p>
                    {sec.comingSoon && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-400 border border-zinc-200">{t('comingSoon')}</span>}
                  </div>
                  <p className="text-xs text-zinc-400 mt-0.5">{sec.desc}</p>
                </div>
                {!sec.comingSoon && <CaretRight size={16} className="text-zinc-300 group-hover:text-zinc-500 transition-colors flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="settings-page">
        <button onClick={() => setActiveSection(null)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-700 mb-6" data-testid="settings-back-btn">
          <ArrowLeft size={14} />
          <span>{t('settings')}</span>
          <span className="text-zinc-300">/</span>
          <span className="font-semibold text-zinc-800">{sections.find(s => s.id === activeSection)?.label}</span>
        </button>
        {activeSection === 'companyProfile' && <CompanyProfileSection t={t} isAdmin={isAdmin} />}
        {activeSection === 'numbering' && <NumberingSection t={t} isAdmin={isAdmin} />}
        {activeSection === 'mobilePush' && <MobilePushSection t={t} isAdmin={isAdmin} />}
      </div>
    </Layout>
  );
}
